﻿// eslint-disable-next-line no-unused-vars
import NavEditor from '../../components/nav/navEditor';
import CuiLayout from '../../cui/layouts/CuiLayout';
import { bind, wire } from '../../../vendors/hyperhtml';
import getResource from '../../core/resources';
import odataFetch from '../../core/odataFetch';
import { applyNavChanges, createNavDataStore } from './navDataConverter';
import tocEditorCuiActions from './tocEditorCuiLayoutActions';
import SvgManager from '../../core/SvgManager';
import xml from '../../core/Xml';
import { getFormChange, getFormData } from './formDataConverter';

export default class TocEditorCuiLayout extends CuiLayout {
	actions = tocEditorCuiActions(this.setState.bind(this));
	initialState = {
		changes: {},
		defaultLanguageCode: 'en',
		languages: [{ code: 'en', name: 'English' }],
		selectedId: '',
		tocData: new Map()
	};

	async init() {
		const contentsTitle = getResource('common.contents');
		const header = wire()`
			<div class="toc-editor__header" />
		`;
		const nav = wire()`
			<aras-nav-editor class="toc-editor__nav" />
		`;
		nav.on('click', (key) => this.actions.selectItem(key));
		const formHeader = wire()`
			<div class="toc-editor__form-header" />
		`;
		const formView = wire()`
			<aras-form-view />
		`;
		bind(this.dom)`
			<div class="toc-editor">
				${header}
				<div class="toc-editor__content">
					<div class="toc-editor__pane toc-editor__nav-container">
						<div class="toc-editor__nav-title">${contentsTitle}</div>
						${nav}
					</div>
					<div class="toc-editor__pane">
						${formHeader}
						${formView}
					<div>
				</div>
			</div>
		`;

		this.dom = header;
		this.nav = nav;
		this.formHeader = formHeader;
		this.formView = formView;
		this.formView.addEventListener('change', (e) => {
			const detail = e.detail;
			if (!detail) {
				return;
			}

			const state = this.state;
			const change = getFormChange(this.formView, state, detail);
			if (!change) {
				return;
			}

			const relshipProperties = [
				'role@aras.id',
				'role@aras.keyed_name',
				'sort_order'
			];
			const changedProperties = Object.keys(change);
			const relshipChanged = relshipProperties.some((property) =>
				changedProperties.includes(property)
			);
			const { changes, selectedId, tocData } = state;
			const relshipItem = {
				...tocData.get(selectedId),
				...changes[selectedId]
			};
			const id = relshipChanged ? relshipItem.id : relshipItem.related_id;
			this.actions.editItem(id, change);
		});

		await Promise.all([this._initTOC(nav), super.init()]);
		this._initLanguageData();
	}

	updateLayout(oldProps, oldState) {
		const { changes: oldChanges, selectedId: oldSelectedId } = oldState;
		const { changes, selectedId } = this.state;
		const data = this._applyDataChanges();
		const hasNewChanegs = oldChanges !== changes;
		if (hasNewChanegs) {
			applyNavChanges(this.nav, data);
		}

		const newItemSelected = oldSelectedId !== selectedId;
		if (newItemSelected) {
			this._selectItem(data);
		}

		if (hasNewChanegs || newItemSelected) {
			this._renderForm(data);
		}
	}

	_selectItem(data) {
		const { changes, selectedId } = this.state;
		const selectedItem = data.get(selectedId);
		if (!selectedItem) {
			return;
		}

		this.nav.select(selectedId);

		const isNewItem = selectedItem['@aras.action'] === 'add';
		if (!isNewItem) {
			return;
		}

		const selectedRelatedId = selectedItem.related_id;
		const selectedRelatedItem = changes[selectedRelatedId];
		const selectedItemParentMenu = selectedRelatedItem['parent_menu@aras.id'];
		const itemToExpand = [...data.values()].find(
			(el) => el.related_id === selectedItemParentMenu
		);

		if (itemToExpand) {
			this.nav.expand(itemToExpand.id, true);
		}
	}

	_applyDataChanges() {
		const { changes, tocData } = this.state;
		const newData = new Map([...tocData]);

		for (const [id, change] of Object.entries(changes)) {
			const oldItem = tocData.get(id);
			if (!oldItem) {
				newData.set(id, change);
				continue;
			}

			newData.set(id, { ...oldItem, ...change });
		}

		return newData;
	}

	async _fetchTOCData() {
		const odataFilter = `action eq 'Add' and source_id/location/name eq 'TOC'`;
		const odataRelatedSelect = `id,image,name,label,parent_menu,additional_data`;
		const requestFormat = `application/json;odata.metadata=none`;
		const requestURL = `
			CommandBarSectionItem?
			$select=id,role,sort_order&
			$orderby=sort_order&
			$format=${requestFormat}&
			$filter=${odataFilter}&
			$expand=related_id($select=${odataRelatedSelect};lang=*)
		`;
		const data = await odataFetch(requestURL);
		return data.value.reduce((acc, relship) => {
			const { id, related_id: relatedItem } = relship;
			acc.set(relatedItem.id, {
				...relatedItem,
				additional_data: JSON.parse(relatedItem.additional_data)
			});
			acc.set(id, {
				...relship,
				'@odata.type': '#CommandBarSectionItem',
				related_id: relatedItem.id
			});
			return acc;
		}, new Map());
	}

	async _initTOC(nav) {
		const tocData = await this._fetchTOCData();
		const dataStore = createNavDataStore(tocData);
		const { dataMap, roots } = dataStore;
		nav.data = dataMap;
		nav.roots = roots;
		this.setState((state) => {
			state.tocData = tocData;
		});
	}
	_renderForm(stateData) {
		const formData = getFormData(stateData, this.state);
		if (!formData) {
			bind(this.formHeader)``;
			this.formView.order = [];
			this.formView.render();
			return;
		}

		const { data, image, label, metadata, order } = formData;
		bind(this.formHeader)`
			${SvgManager.createHyperHTMLNode(image, {
				class: 'toc-editor__form-header-image'
			})}
			<span class="toc-editor__form-header-label">${label}</span>
		`;

		this.formView.metadata = metadata;
		this.formView.data = data;
		this.formView.order = order;
		this.formView.render();
	}

	_initLanguageData() {
		this.setState((state) => {
			state.defaultLanguageCode = aras.getSessionContextLanguageCode();
			state.languages = xml
				.selectNodes(aras.getLanguagesResultNd(), 'Item[@type="Language"]')
				.map((language) => {
					const codePropertyNode = xml.selectSingleNode(language, 'code');
					const code = codePropertyNode?.text;
					const namePropertyNode = xml.selectSingleNode(language, 'name');
					const name = namePropertyNode?.text;
					return { code, name };
				});
		});
	}
}
